<script setup>
import FormElementMixins from '@/mixins/FormElementMixins'

/**
 * We use a script setup section to define reactive logic.
 * Note: Mixins work with Options API, but we can combine them or 
 * move logic to Composables for cleaner Vue 3 code.
 */
</script>

<template>
    <div>
        <div class="mb-3" v-if="!iscollapsed">
            <label :for="`field${element.type}${index}`" class="form-label" v-if="fieldTitle">
                {{ fieldTitle }}:
            </label>
            
            <!-- 
                The select model is bound to element.value. 
                When '_custom_' is selected, we trigger the custom mode.
            -->
            <select 
                class="form-select" 
                :id="element.id" 
                v-model="element.value" 
                :name="element.name" 
                :required="element.required" 
                ref="select" 
                @change="handleSelectChange"
            >
                <option value="" v-if="element.placeholder !== undefined" disabled>
                    {{ element.placeholder }}
                </option>
                <option 
                    v-for="(option, idx) in element.options" 
                    :key="`${option.value}-${index}-${idx}`"
                    :value="option.value"
                    :data-index="index"
                    :data-section="option.section" 
                    :data-children="JSON.stringify(option.children)"
                >
                    {{ option.title }}
                </option>
            </select>

            <!-- 
                The custom input appears when isCustomActive is true.
                It stays visible until the user selects a non-custom value from the dropdown.
            -->
            <div v-if="isCustomActive">
                <input 
                    class="form-control mt-2" 
                    type="text" 
                    :name="`${element.name}-custom`" 
                    v-model="customInputValue"
                    placeholder="Enter custom value..."
                    @input="updateElementValue"
                >
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'FormElementSelect',
    mixins: [FormElementMixins],
    data() {
        return {
            isCustomActive: false,
            customInputValue: ''
        }
    },
    methods: {
        syncCustomState() {
            const optionExists = this.element.options.some(opt => opt.value === this.element.value);
            
            // Handle the '_custom_' option case
            if (this.element.value === '_custom_') {
                this.isCustomActive = true;
                this.customInputValue = '';
                return;
            }
            
            // Handle normal custom value case (value not in options)
            this.isCustomActive = Boolean(this.element.value) && !optionExists && this.element.value !== "";

            if (this.isCustomActive) {
                this.customInputValue = this.element.value;
            } else {
                this.customInputValue = '';
            }
        },
        handleSelectChange(event) {
            if (this.element.value === '_custom_') {
                this.isCustomActive = true;
                // Set custom input to empty - user will type their custom value
                this.customInputValue = '';
                // Don't clear element.value immediately to avoid watch overriding isCustomActive
                // It will be cleared when user starts typing in custom input
            } else {
                // If they select a real option, hide the custom input
                this.isCustomActive = false;
            }
            
            this.$emit('selectChanged', event);
        },
        updateElementValue() {
            // Update the actual element value as the user types
            this.element.value = this.customInputValue;
        }
    },
    watch: {
        'element.value'() {
            this.syncCustomState();
        }
    },
    mounted() {
        // Handle initial state if value doesn't match predefined options
        this.syncCustomState();

        if (this.element.value) {
            this.$emit('selectChanged', { target: this.$refs.select });
        }
    }
}
</script>

<style scoped>
/* Add component specific styles here */
</style>
